<script setup>
import { ref, computed } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength, helpers } from '@vuelidate/validators';
import {
  maskWhatsapp,
  formatWhatsapp,
  isValidWhatsapp,
} from 'dashboard/helper/whatsappMask';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Auth from '../../../../api/auth';
import wootConstants from 'dashboard/constants/globals';
import SetPasswordModal from './SetPasswordModal.vue';

const props = defineProps({
  id: {
    type: Number,
    required: true,
  },
  name: {
    type: String,
    required: true,
  },
  email: {
    type: String,
    default: '',
  },
  type: {
    type: String,
    default: '',
  },
  availability: {
    type: String,
    default: '',
  },
  customRoleId: {
    type: Number,
    default: null,
  },
  isHuman: {
    type: Boolean,
    default: true,
  },
  whatsapp: {
    type: String,
    default: '',
  },
  hasLimitedAccess: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close']);

const { AVAILABILITY_STATUS_KEYS } = wootConstants;

const store = useStore();
const { t } = useI18n();

const agentName = ref(props.name);
const agentAvailability = ref(props.availability);
const selectedRoleId = ref(props.customRoleId || props.type);
const agentCredentials = ref({ email: props.email });
const isAI = ref(!props.isHuman);
const agentWhatsapp = ref(formatWhatsapp(props.whatsapp));
const showPasswordModal = ref(false);

const rules = {
  agentName: { required, minLength: minLength(1) },
  selectedRoleId: { required },
  agentAvailability: { required },
  agentWhatsapp: {
    valid: helpers.withMessage(
      () => t('AGENT_MGMT.EDIT.FORM.WHATSAPP.ERROR'),
      isValidWhatsapp
    ),
  },
};

const v$ = useVuelidate(rules, {
  agentName,
  selectedRoleId,
  agentAvailability,
  agentWhatsapp,
});

// Apagando, não reformata — senão o backspace trava em ")" e "-".
const onWhatsappInput = e => {
  if (!e.inputType?.startsWith('delete')) {
    agentWhatsapp.value = maskWhatsapp(e.target.value);
  }
};

const pageTitle = computed(
  () => `${t('AGENT_MGMT.EDIT.TITLE')} - ${props.name}`
);

const uiFlags = useMapGetter('agents/getUIFlags');
const getCustomRoles = useMapGetter('customRole/getCustomRoles');

const roles = computed(() => {
  const defaultRoles = [
    {
      id: 'administrator',
      name: 'administrator',
      label: t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR'),
    },
    {
      id: 'agent',
      name: 'agent',
      label: t('AGENT_MGMT.AGENT_TYPES.AGENT'),
    },
  ];

  const customRoles = getCustomRoles.value.map(role => ({
    id: role.id,
    name: `custom_${role.id}`,
    label: role.name,
  }));

  return [...defaultRoles, ...customRoles];
});

const selectedRole = computed(() =>
  roles.value.find(
    role =>
      role.id === selectedRoleId.value || role.name === selectedRoleId.value
  )
);

const statusList = computed(() => {
  return [
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.ONLINE'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.BUSY'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.OFFLINE'),
  ];
});

const availabilityStatuses = computed(() =>
  statusList.value.map((statusLabel, index) => ({
    label: statusLabel,
    value: AVAILABILITY_STATUS_KEYS[index],
    disabled: props.availability === AVAILABILITY_STATUS_KEYS[index],
  }))
);

const editAgent = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  try {
    const payload = {
      id: props.id,
      availability: agentAvailability.value,
    };

    // Se o usuário não tem acesso limitado, pode editar todos os campos
    if (!props.hasLimitedAccess) {
      payload.name = agentName.value;
      payload.whatsapp = agentWhatsapp.value;
      payload.is_human = !isAI.value;

      if (selectedRole.value.name.startsWith('custom_')) {
        payload.custom_role_id = selectedRole.value.id;
      } else {
        payload.role = selectedRole.value.name;
        payload.custom_role_id = null;
      }
    }

    await store.dispatch('agents/update', payload);
    useAlert(t('AGENT_MGMT.EDIT.API.SUCCESS_MESSAGE'));
    emit('close');
  } catch (error) {
    // 422 de validação (ex.: WhatsApp inválido) traz o motivo em `message`.
    useAlert(
      error?.response?.data?.message || t('AGENT_MGMT.EDIT.API.ERROR_MESSAGE')
    );
  }
};

const resetPassword = async () => {
  try {
    await Auth.resetPassword(agentCredentials.value);
    useAlert(t('AGENT_MGMT.EDIT.PASSWORD_RESET.ADMIN_SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('AGENT_MGMT.EDIT.PASSWORD_RESET.ERROR_MESSAGE'));
  }
};

const openSetPasswordModal = () => {
  showPasswordModal.value = true;
};

const closePasswordModal = () => {
  showPasswordModal.value = false;
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto" style="min-height: 500px;">
    <woot-modal-header :header-title="pageTitle" />
    <form class="w-full" @submit.prevent="editAgent">
      <div class="w-full">
        <label :class="{ error: v$.agentName.$error }">
          {{ $t('AGENT_MGMT.EDIT.FORM.NAME.LABEL') }}
          <input
            v-model="agentName"
            type="text"
            :placeholder="$t('AGENT_MGMT.EDIT.FORM.NAME.PLACEHOLDER')"
            :disabled="hasLimitedAccess"
            @input="v$.agentName.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.selectedRoleId.$error }">
          {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_TYPE.LABEL') }}
          <select
            v-model="selectedRoleId"
            :disabled="hasLimitedAccess"
            @change="v$.selectedRoleId.$touch"
          >
            <option v-for="role in roles" :key="role.id" :value="role.id">
              {{ role.label }}
            </option>
          </select>
          <span v-if="v$.selectedRoleId.$error" class="message">
            {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_TYPE.ERROR') }}
          </span>
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.agentWhatsapp.$error }">
          {{ $t('AGENT_MGMT.EDIT.FORM.WHATSAPP.LABEL') }}
          <input
            v-model="agentWhatsapp"
            type="tel"
            inputmode="tel"
            maxlength="20"
            :placeholder="$t('AGENT_MGMT.EDIT.FORM.WHATSAPP.PLACEHOLDER')"
            :disabled="hasLimitedAccess"
            @input="onWhatsappInput"
            @blur="v$.agentWhatsapp.$touch"
          />
          <span v-if="v$.agentWhatsapp.$error" class="message">
            {{ v$.agentWhatsapp.$errors[0]?.$message }}
          </span>
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.agentAvailability.$error }">
          {{ $t('PROFILE_SETTINGS.FORM.AVAILABILITY.LABEL') }}
          <select
            v-model="agentAvailability"
            @change="v$.agentAvailability.$touch"
          >
            <option
              v-for="status in availabilityStatuses"
              :key="status.value"
              :value="status.value"
            >
              {{ status.label }}
            </option>
          </select>
          <span v-if="v$.agentAvailability.$error" class="message">
            {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_AVAILABILITY.ERROR') }}
          </span>
        </label>
      </div>

      <!-- Checkbox para indicar se é IA -->
      <div class="w-full" style="min-height: 60px;">
        <div v-if="selectedRoleId !== 'administrator'" class="flex items-center gap-3 py-2">
          <Checkbox v-model="isAI" :disabled="hasLimitedAccess" />
          <label
            class="text-sm font-medium"
            :class="{ 'cursor-pointer': !hasLimitedAccess, 'opacity-50': hasLimitedAccess }"
            @click="!hasLimitedAccess && (isAI = !isAI)"
          >
            Este é um agente de IA
          </label>
        </div>
      </div>

      <div class="flex flex-row justify-start w-full gap-2 px-0 py-2">
        <div v-if="!hasLimitedAccess" class="w-[50%] ltr:text-left rtl:text-right flex gap-2">
          <!-- Botão de enviar email para resetar senha -->
          <Button
            ghost
            type="button"
            v-tooltip.center="$t('AGENT_MGMT.EDIT.PASSWORD_RESET.ADMIN_RESET_BUTTON')"
            icon="i-lucide-mail"
            class="!px-2"
            :label="$t('AGENT_MGMT.EDIT.PASSWORD_RESET.ADMIN_RESET_BUTTON')"
            @click.prevent="resetPassword"
          />
          <!-- Botão de definir senha diretamente -->
          <Button
            ghost
            type="button"
            v-tooltip.center="$t('AGENT_MGMT.EDIT.PASSWORD_SET.BUTTON')"
            icon="i-lucide-lock-keyhole"
            class="!px-2"
            :label="$t('AGENT_MGMT.EDIT.PASSWORD_SET.BUTTON')"
            @click.prevent="openSetPasswordModal"
          />
        </div>
        <div :class="hasLimitedAccess ? 'w-full' : 'w-[50%]'" class="flex justify-end items-center gap-2">
          <Button
            faded
            slate
            type="reset"
            :label="$t('AGENT_MGMT.EDIT.CANCEL_BUTTON_TEXT')"
            @click.prevent="emit('close')"
          />
          <Button
            type="submit"
            :label="$t('AGENT_MGMT.EDIT.FORM.SUBMIT')"
            :disabled="v$.$invalid || uiFlags.isUpdating"
            :is-loading="uiFlags.isUpdating"
          />
        </div>
      </div>
    </form>

    <!-- Modal de Definir Senha -->
    <woot-modal
      v-model:show="showPasswordModal"
      :on-close="closePasswordModal"
    >
      <SetPasswordModal
        :agent-id="id"
        :agent-name="name"
        @close="closePasswordModal"
      />
    </woot-modal>
  </div>
</template>